class AutoGiftManager {
  constructor() {
    this.settings = window.themeVariables?.settings || {};
    this.giftTiers = this.buildGiftTiers();
    this.isProcessing = false;
    this.lastActionTime = 0;
    this.operationQueue = Promise.resolve();

    if (this.giftTiers.length === 0) {
      return;
    }

    this.init();
  }

  buildGiftTiers() {
    const tiers = [];

    if (this.settings.cartAutoGiftEnabled === true || this.settings.cartAutoGiftEnabled === 'true') {
      tiers.push({
        key: 'gift1',
        threshold: parseInt(this.settings.cartAutoGiftThreshold, 10) || 8000,
        productId: this.settings.cartAutoGiftProduct,
        productHandle: this.settings.cartAutoGiftProductHandle,
        firstAvailableVariantId: this.settings.cartAutoGiftProductFirstAvailableVariantId,
        firstVariantId: this.settings.cartAutoGiftProductFirstVariantId,
        message: this.settings.cartAutoGiftMessage || 'Free gift added to your cart!',
        badgeText: this.settings.cartAutoGiftBadgeText || 'Free Gift'
      });
    }

    if (this.settings.cartAutoGiftSecondEnabled === true || this.settings.cartAutoGiftSecondEnabled === 'true') {
      tiers.push({
        key: 'gift2',
        threshold: parseInt(this.settings.cartAutoGiftSecondThreshold, 10) || 12000,
        productId: this.settings.cartAutoGiftSecondProduct,
        productHandle: this.settings.cartAutoGiftSecondProductHandle,
        firstAvailableVariantId: this.settings.cartAutoGiftSecondProductFirstAvailableVariantId,
        firstVariantId: this.settings.cartAutoGiftSecondProductFirstVariantId,
        message: this.settings.cartAutoGiftSecondMessage || 'Second free gift added to your cart!',
        badgeText: this.settings.cartAutoGiftSecondBadgeText || 'Free Gift'
      });
    }

    return tiers.filter((tier) => tier.productId && tier.threshold > 0).sort((a, b) => a.threshold - b.threshold);
  }

  init() {
    document.addEventListener('cart:change', (event) => {
      this.handleCartChange(event);
    });

    document.addEventListener('variant:add', (event) => {
      this.handleCartChange(event);
    });

    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => this.checkCartForAutoGift(), 500);
    });

    setTimeout(() => this.checkCartForAutoGift(), 1000);
    setTimeout(() => this.checkCartForAutoGift(), 3000);
  }

  async handleCartChange(event) {
    if (this.isProcessing) {
      return;
    }

    if (event.detail?.baseEvent === 'auto-gift:add' || event.detail?.baseEvent === 'auto-gift:remove') {
      return;
    }

    const cart = event.detail?.cart;

    if (!cart) {
      await this.checkCartForAutoGift();
      return;
    }

    await this.checkCartForAutoGift(cart);
  }

  async checkCartForAutoGift(cartData = null) {
    this.operationQueue = this.operationQueue.then(async () => {
      if (this.isProcessing) {
        return;
      }

      const now = Date.now();
      if (now - this.lastActionTime < 500) {
        return;
      }

      return this.executeCartCheck(cartData);
    }).catch(() => {});

    return this.operationQueue;
  }

  async executeCartCheck(cartData = null) {
    let cart = cartData || await this.fetchCart();

    for (const tier of this.giftTiers) {
      const hasGiftProduct = this.hasGiftProductInCart(cart, tier);
      const shouldAdd = cart.total_price >= tier.threshold && !hasGiftProduct;
      const shouldRemove = cart.total_price < tier.threshold && hasGiftProduct;

      if (shouldAdd) {
        await this.addGiftProduct(tier);
        cart = await this.fetchCart();
      } else if (shouldRemove) {
        await this.removeGiftProduct(cart, tier);
        cart = await this.fetchCart();
      }
    }
  }

  async fetchCart() {
    const response = await fetch(`${Shopify.routes.root}cart.js`);
    return response.json();
  }

  hasGiftProductInCart(cart, tier) {
    const autoGiftItems = cart.items.filter((item) => {
      return item.product_id.toString() === tier.productId.toString() && item.properties && item.properties._auto_gift === 'true';
    });

    if (autoGiftItems.length > 0) {
      return true;
    }

    return cart.items.some((item) => item.product_id.toString() === tier.productId.toString());
  }

  async resolveVariantId(tier) {
    if (tier.firstAvailableVariantId || tier.firstVariantId) {
      return tier.firstAvailableVariantId || tier.firstVariantId;
    }

    const handlePath = tier.productHandle ? tier.productHandle : tier.productId;
    const response = await fetch(`${Shopify.routes.root}products/${handlePath}.js`);

    if (!response.ok) {
      return null;
    }

    const product = await response.json();
    if (!product || !product.variants || product.variants.length === 0) {
      return null;
    }

    const availableVariant = product.variants.find((variant) => variant.available) || product.variants[0];
    return availableVariant.id;
  }

  async addGiftProduct(tier) {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    try {
      const preCheckCart = await this.fetchCart();
      const existingProduct = preCheckCart.items.some((item) => item.product_id.toString() === tier.productId.toString());

      if (existingProduct) {
        return;
      }

      const selectedVariantId = await this.resolveVariantId(tier);
      if (!selectedVariantId) {
        return;
      }

      const addResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: selectedVariantId,
          quantity: 1,
          properties: {
            '_auto_gift': 'true',
            '_gift_tier': tier.key,
            '_gift_message': tier.message,
            '_gift_badge_text': tier.badgeText
          }
        })
      });

      if (!addResponse.ok) {
        return;
      }

      this.lastActionTime = Date.now();
      const updatedCart = await this.fetchBundledCart();
      this.dispatchCartChange('auto-gift:add', updatedCart);
    } catch (_) {
      // Intentionally swallowed to keep cart UX resilient.
    } finally {
      setTimeout(() => {
        this.isProcessing = false;
      }, 1000);
    }
  }

  async removeGiftProduct(cart, tier) {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    try {
      const giftItem = cart.items.find((item) => {
        return item.product_id.toString() === tier.productId.toString()
          && item.properties
          && item.properties._auto_gift === 'true'
          && (!item.properties._gift_tier || item.properties._gift_tier === tier.key);
      });

      if (!giftItem) {
        return;
      }

      const removeResponse = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0
        })
      });

      if (!removeResponse.ok) {
        return;
      }

      this.lastActionTime = Date.now();
      const updatedCart = await this.fetchBundledCart();
      this.dispatchCartChange('auto-gift:remove', updatedCart);
    } catch (_) {
      // Intentionally swallowed to keep cart UX resilient.
    } finally {
      setTimeout(() => {
        this.isProcessing = false;
      }, 1000);
    }
  }

  async fetchBundledCart() {
    const sectionsToBundle = [];

    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    const updatedCart = await this.fetchCart();

    if (sectionsToBundle.length === 0) {
      return updatedCart;
    }

    try {
      const sectionsResponse = await fetch(`${Shopify.routes.root}?sections=${sectionsToBundle.join(',')}`);
      const sectionsText = await sectionsResponse.text();

      try {
        updatedCart.sections = JSON.parse(sectionsText);
      } catch (_) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = sectionsText;
        updatedCart.sections = {};

        sectionsToBundle.forEach((sectionId) => {
          const sectionElement = tempDiv.querySelector(`#shopify-section-${sectionId}`);
          if (sectionElement) {
            updatedCart.sections[sectionId] = sectionElement.outerHTML;
          }
        });
      }
    } catch (_) {
      // Keep cart data even if section bundle refresh fails.
    }

    return updatedCart;
  }

  dispatchCartChange(baseEvent, cart) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent,
        cart
      }
    }));
  }
}

let autoGiftManagerInstance = null;

function initializeAutoGift() {
  if (autoGiftManagerInstance) {
    return;
  }

  if (typeof Shopify === 'undefined' || !window.themeVariables) {
    setTimeout(initializeAutoGift, 500);
    return;
  }

  autoGiftManagerInstance = new AutoGiftManager();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAutoGift);
} else {
  initializeAutoGift();
}

setTimeout(() => {
  initializeAutoGift();
}, 2000);
